<template>
  <nv-tab-page>
    <div
      v-if="$auth.hasPermissions('owl.marketview') && countryCode === '*'"
      class="d-flex justify-start mb-4"
    >
      <v-btn color="secondary" dark @click="showAddDialog = true">
        <v-icon left>mdi-plus</v-icon>
        Add Localization
      </v-btn>
    </div>

    <v-data-table
      :headers="headers()"
      :items="items"
      :loading="loading"
      :options.sync="table"
      :server-items-length="totalItems"
      :footer-props="footerProps"
      :items-per-page.sync="table.itemsPerPage"
    >
      <template #item.pattern="{ item }">
        <div class="d-flex justify-space-between align-center">
          <router-link :to="{ name: `owl.${kind}.rules.show`, params: { ruleId: item.id } }">
            {{ item.pattern }}
          </router-link>
          <v-btn v-if="kind === 'domain'" :href="'https://' + item.pattern" icon target="_blank">
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
      </template>

      <template #item.updated="{ item }">
        <updated-details :item="item" />
      </template>

      <template #item.status="{ item }">
        {{ item.status | humanize }}
      </template>
      <template #item.country="{ item }">
        <CountryBadge :country="resolveCountryCode(item.localization)" />
      </template>
    </v-data-table>

    <!-- Add Localization Dialog -->
    <v-dialog :value="showAddDialog" persistent max-width="600px">
      <nv-form
        :errors="formErrors"
        title="Add Localization"
        :loading="loading"
        @cancel="closeDialog"
        @nv-submit="saveLocalization"
      >
        <v-row>
          <v-col cols="12">
            <v-select
              v-model="formData.localization"
              :items="countryOptions"
              :rules="rules.localization"
              label="Country"
              item-text="name"
              item-value="code"
            />
          </v-col>
          <v-col cols="12">
            <tree-search
              v-model="formData.tree_id"
              :rules="rules.tree_id"
              mode="classification"
              :country-code="formData.localization"
              skip-purpose
            />
          </v-col>
          <v-col cols="12">
            <owl-date-picker
              v-model="formData.effective_start_date"
              label="Effective Start Date"
              clearable
            />
          </v-col>
        </v-row>
      </nv-form>
    </v-dialog>
  </nv-tab-page>
</template>

<script>
import HTTP from '@/api/ant';
import { TableMixin, FormMixin } from '@nielsen-media/ui-toolkit-vue-api';
import UpdatedDetails from '@/components/cls/UpdatedDetails.vue';
import { resolveCountryCode } from '@/helpers/filters';
import CountryBadge from '@/components/CountryBadge';
import TreeSearch from '@/components/mv/TreeSearch';
import OwlDatePicker from '@/components/OwlDatePicker';
import Rules from 'bsm-validation-rules';

const defaultHeaders = [
  { text: 'ID', sortable: true, value: 'id' },
  { text: 'Pattern', sortable: false, value: 'pattern' },
  { text: 'Status', sortable: false, value: 'status' },
  { text: 'Updated', sortable: true, value: 'updated' },
  { text: 'Market', sortable: false, value: 'country' },
];
const iosHeaders = [
  { text: 'ID', sortable: true, value: 'id' },
  { text: 'Pattern', sortable: false, value: 'pattern' },
  { text: 'Match Type', sortable: false, value: 'match_type' },
  { text: 'User Agents', sortable: false, value: 'ua' },
  { text: 'Domain', sortable: false, value: 'domain' },
  { text: 'Status', sortable: false, value: 'status' },
  { text: 'Updated', sortable: true, value: 'updated' },
  { text: 'Market', sortable: false, value: 'country' },
];

const kinds = ['domain', 'ottm', 'video', 'android', 'ios'];

const rules = {
  localization: [Rules.presence()],
  tree_id: [Rules.presence()],
};

export default {
  components: {
    CountryBadge,
    UpdatedDetails,
    TreeSearch,
    OwlDatePicker,
  },
  mixins: [TableMixin, FormMixin],
  props: {
    id: { type: Number, required: true },
    kind: {
      type: String,
      required: true,
      validator: v => kinds.indexOf(v) !== -1,
    },
    countryCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tableParam: 'patterns.dt',
      showAddDialog: false,
      countryOptions: [{ name: 'US', code: 'us' }],
      formData: {
        localization: 'us',
        tree_id: null,
        effective_start_date: null,
      },
      rules,
    };
  },
  methods: {
    tableFetch() {
      this.paginateTableData(
        HTTP,
        `/${this.kind}/rules/${this.id}/localized?localization=${this.countryCode}`,
      );
    },
    headers() {
      return this.kind === 'ios' ? iosHeaders : defaultHeaders;
    },
    resolveCountryCode,
    closeDialog() {
      this.showAddDialog = false;
      this.formData = {
        localization: 'us',
        tree_id: null,
        effective_start_date: null,
      };
    },
    saveLocalization() {
      this.loading = true;
      const payload = {
        effective_start_date: this.formData.effective_start_date,
        tree_id: this.formData.tree_id,
        localization: this.formData.localization,
      };

      HTTP.post(`/${this.kind}/rule/${this.id}/localized`, payload, { niSkipFlash: true })
        .then(() => {
          this.$flash('success', 'Localization added successfully');
          this.closeDialog();
          this.tableFetch(); // Refresh the table
        })
        .catch(err => {
          if (err.response?.status === 400 && err.response?.data?.errors != null) {
            const key = Object.keys(err.response.data.errors)[0];
            this.$flash('error', `${key} ${err.response.data.errors[key]}`);
          } else {
            this.$flash('error', 'Failed to add localization');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
